import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const chainElements = [
  { label: '评价结论', icon: 'ri-medal-line' },
  { label: '维度', icon: 'ri-bar-chart-2-line' },
  { label: '证据卡', icon: 'ri-file-text-line' },
  { label: '课堂原话', icon: 'ri-mic-line' },
  { label: '时间戳', icon: 'ri-timer-line' },
  { label: '视频片段', icon: 'ri-video-line' },
  { label: '资料来源', icon: 'ri-folder-shield-line' },
];

const ledgerStatuses = [
  { label: '成立', color: 'bg-green-50 text-green-700 border-green-200' },
  { label: '存疑', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { label: '无法核验', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  { label: '待教研核实', color: 'bg-slate-50 text-slate-700 border-slate-200' },
];

const ledgerExample = [
  { claim: '"世界现存政党至少6000个"', status: '存疑、待教研核实' },
  { claim: '"稳定存续百年政党67个"', status: '存疑、待教研核实' },
  { claim: '"世界大党约20个"', status: '无法核验（口径主观）' },
];

export default function P39EvidenceChain({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <span className="inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700">可追溯性</span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            证据链与<span className="text-primary-600">事实账本</span>
          </h2>
          <p className="text-sm text-foreground-500 font-body mt-2">每个结论都能被追问</p>
        </div>

        {/* 证据链流程 */}
        <div className="max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-200">
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2">
            {chainElements.map((el, i) => (
              <div key={el.label} className="flex items-center gap-0">
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl glass-liquid border-primary-200/30">
                  <span className="font-mono text-[10px] font-bold text-primary-400">{i + 1}</span>
                  <i className={`${el.icon} text-sm text-primary-500`} />
                  <span className="text-xs font-body font-semibold text-foreground-700 whitespace-nowrap">{el.label}</span>
                </div>
                {i < chainElements.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-300 text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 两栏：账本状态 + 实例 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-400">
          {/* 四种状态 */}
          <div className="p-4 rounded-2xl glass-liquid border-primary-200/30">
            <div className="relative z-10">
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-3">事实账本状态</h3>
              <div className="grid grid-cols-2 gap-2">
                {ledgerStatuses.map((s) => (
                  <div key={s.label} className={`px-3 py-2 rounded-lg border text-center ${s.color}`}>
                    <span className="text-[11px] font-body font-semibold">{s.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-foreground-400 font-body mt-3">
                存疑≠教师讲错，而是资料口径和来源需进一步确认
              </p>
            </div>
          </div>

          {/* 本科01实例 */}
          <div className="p-4 rounded-2xl glass-liquid-gold border-accent-200/30">
            <div className="relative z-10">
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
                <span className="text-xs text-foreground-400">本科01</span>
                18条事实账本
              </h3>
              <div className="space-y-2">
                {ledgerExample.map((ex, i) => (
                  <div key={i} className="flex items-start justify-between p-2.5 rounded-lg bg-background-50/60">
                    <span className="text-[11px] text-foreground-700 font-body">{ex.claim}</span>
                    <span className="text-[10px] text-amber-600 font-body font-semibold whitespace-nowrap ml-2">{ex.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="max-w-3xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass border-white/50">
            <i className="ri-loop-left-line text-primary-500 text-sm" />
            <span className="text-xs font-body text-foreground-500">
              可追问、可反驳、可修订——比"模型永远正确"更有价值
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}